import { RefObject } from 'react';
import { toPng } from 'html-to-image';
import { exportChartToPdf } from '../utils/exportUtils';

/**
 * Options for chart interactions
 */
export interface ChartInteractionOptions {
  /** Enable zooming functionality */
  enableZoom?: boolean;
  /** Enable panning functionality */
  enablePan?: boolean;
  /** Enable export functionality */
  enableExport?: boolean;
  /** Minimum zoom scale */
  minScale?: number;
  /** Maximum zoom scale */
  maxScale?: number;
}

/**
 * Default interaction options
 */
export const DEFAULT_INTERACTION_OPTIONS: ChartInteractionOptions = {
  enableZoom: true,
  enablePan: true,
  enableExport: true,
  minScale: 0.5,
  maxScale: 4
};

/**
 * Export a chart to PNG
 * @param chartRef - Reference to the chart component
 * @param filename - Name for the exported file
 */
export const exportChartToPng = async (
  chartRef: RefObject<HTMLDivElement | null>,
  filename: string
): Promise<string | null> => {
  if (!chartRef.current) return null;
  
  try {
    // Convert the chart to a PNG data URL
    const dataUrl = await toPng(chartRef.current, {
      quality: 0.95,
      backgroundColor: '#fff'
    });
    
    // Create a download link and trigger it
    const link = document.createElement('a');
    link.download = `${filename.replace(/\s+/g, '_')}.png`;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return dataUrl;
  } catch (error) {
    console.error('Error exporting chart to PNG:', error);
    return null;
  }
};

/**
 * Export chart data to CSV
 * @param chartData - Data used in the chart
 * @param filename - Name for the exported file
 */
export const exportChartDataToCsv = (
  chartData: any[],
  filename: string
): void => {
  // Extract headers from the first data item
  if (!chartData || chartData.length === 0) return;
  
  const headers = Object.keys(chartData[0]).filter(key => key !== 'timestamp');
  
  // Extract rows from data
  const rows = chartData.map(item => {
    return headers.map(header => item[header]);
  });
  
  // Create CSV content
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => {
      // Handle special characters and commas in the data
      const cellStr = String(cell);
      return cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')
        ? `"${cellStr.replace(/"/g, '""')}"`
        : cellStr;
    }).join(','))
  ].join('\n');
  
  // Create a blob with the CSV content
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  
  // Create a download link and trigger it
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename.replace(/\s+/g, '_')}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Handle export of a chart to PDF
 * @param chartRef - Reference to the chart component
 * @param chartData - Data used in the chart
 * @param filename - Name for the exported file
 * @param title - Optional title for the PDF
 * @param includeDataTable - Whether to include a data table in the PDF
 */
export const handleChartExportToPdf = async (
  chartRef: RefObject<HTMLDivElement | null>,
  chartData: any[],
  filename: string,
  title?: string,
  includeDataTable: boolean = false
): Promise<void> => {
  if (!chartRef.current) return;
  
  // If including data table, prepare the data
  let additionalData: { headers: string[], data: (string | number)[][] } | undefined;
  
  if (includeDataTable && chartData && chartData.length > 0) {
    // Extract headers from the first data item
    const headers = Object.keys(chartData[0])
      .filter(key => key !== 'timestamp')
      .map(key => key.charAt(0).toUpperCase() + key.slice(1)); // Capitalize headers
    
    // Extract rows from data
    const data = chartData.map(item => {
      return Object.keys(item)
        .filter(key => key !== 'timestamp')
        .map(key => item[key]);
    });
    
    additionalData = { headers, data };
  }
  
  // Export the chart to PDF
  await exportChartToPdf(
    chartRef.current,
    filename,
    title,
    additionalData
  );
};

export default {
  exportChartToPng,
  exportChartDataToCsv,
  handleChartExportToPdf
};
